import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, Plus, Play, Pause, Archive, Trash2, ChevronRight, X, Calendar,
  Clock, Users, Mail, ArrowRight, Send, Check, Loader2, Settings, Edit3, Upload, FileText,
} from "lucide-react";

const STATUS_COLORS = {
  draft:     "bg-[#F2ECE0] text-[#6B6558]",
  active:    "bg-[#D8ECD9] text-[#245C25]",
  paused:    "bg-[#F4E5C7] text-[#7A5410]",
  archived:  "bg-[#EBE8DF] text-[#8A8471]",
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);   // full campaign detail
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/campaigns");
      setCampaigns(r.data.campaigns || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load campaigns");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (cid) => {
    try {
      const r = await api.get(`/admin/campaigns/${cid}`);
      setSelected(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load campaign");
    }
  };

  const refresh = async () => {
    await load();
    if (selected) openDetail(selected.id);
  };

  return (
    <div className="space-y-8" data-testid="admin-campaigns">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-serif text-[#1A1A1A] flex items-center gap-3">
            <Megaphone size={28} className="text-[#8B4513]" />
            Email Campaigns
          </h1>
          <p className="text-sm text-[#6B6558] mt-1 max-w-2xl">
            Design multi-step email sequences. Enroll contacts, clients, or prospects any time —
            the scheduler handles the sends with built-in suppression and unsubscribe compliance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const res = await api.get("/admin/guides/email-campaigns.pdf",
                                          { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data],
                                                      { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = "Byrd-CO-Email-Campaigns-Guide.pdf";
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                toast.error("Could not download the guide — try again in a moment.");
              }
            }}
            data-testid="download-guide-btn"
            className="inline-flex items-center gap-2 border border-[#D6CDB8] text-[#1A1A1A] px-3 py-2 rounded-full text-xs hover:bg-[#F5F1E8] transition"
            title="PDF walkthrough of CSV upload, column mapping and tokens"
          >
            <FileText size={14} /> Download Guide (PDF)
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-campaign-btn"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm hover:bg-[#333] transition"
          >
            <Plus size={16} /> New campaign
          </button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-[#6B6558]"><Loader2 className="animate-spin" size={16}/> Loading campaigns…</div>
      ) : campaigns.length === 0 ? (
        <div className="border border-dashed border-[#D6CDB8] rounded-2xl p-16 text-center">
          <Megaphone size={40} className="mx-auto text-[#B8AF97]" />
          <div className="mt-4 font-serif text-xl text-[#1A1A1A]">No campaigns yet</div>
          <p className="text-sm text-[#6B6558] mt-2 max-w-md mx-auto">
            Create your first campaign — a name, one or more email steps with day-offsets or absolute
            dates, and you can enroll anyone in the CRM.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-6 inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm"
            data-testid="empty-new-campaign-btn"
          >
            <Plus size={16} /> Create campaign
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {campaigns.map((c) => (
            <button
              key={c.id}
              onClick={() => openDetail(c.id)}
              className="text-left bg-[#FBF7EE] border border-[#E8DFC6] rounded-xl p-4 hover:border-[#8B4513] transition group"
              data-testid={`campaign-row-${c.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif text-lg text-[#1A1A1A] truncate">{c.name}</div>
                    <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || "bg-gray-100"}`}>
                      {c.status}
                    </span>
                  </div>
                  {c.description && (
                    <div className="text-xs text-[#6B6558] mt-1 line-clamp-2">{c.description}</div>
                  )}
                  <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#6B6558]">
                    <span className="inline-flex items-center gap-1"><Mail size={12}/> {c.step_count} step{c.step_count === 1 ? "" : "s"}</span>
                    <span className="inline-flex items-center gap-1"><Users size={12}/> {c.enrollments_total} enrolled</span>
                    <span>Created {fmtRelative(c.created_at)}</span>
                  </div>
                </div>
                <ChevronRight size={20} className="text-[#B8AF97] group-hover:text-[#8B4513] flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCampaignDialog
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setShowCreate(false); load().then(() => openDetail(c.id)); }}
        />
      )}
      {selected && (
        <CampaignDetailDrawer
          campaign={selected}
          onClose={() => setSelected(null)}
          onChange={refresh}
        />
      )}
    </div>
  );
}


// ============================================================
// Create dialog
// ============================================================
function CreateCampaignDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState("mixed");
  const [dailyLimit, setDailyLimit] = useState(500);
  const [weekdaysOnly, setWeekdaysOnly] = useState(false);
  const [timezone, setTimezone] = useState("America/Chicago");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const r = await api.post("/admin/campaigns", {
        name: name.trim(),
        description: description.trim() || null,
        audience_type: audience,
        daily_send_limit: Number(dailyLimit) || 500,
        send_weekdays_only: weekdaysOnly,
        send_timezone: weekdaysOnly ? (timezone.trim() || null) : null,
      });
      toast.success("Campaign created");
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create campaign");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-campaign-dialog">
      <form onSubmit={submit} className="bg-[#F5F1E8] rounded-2xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-serif text-[#1A1A1A]">New campaign</h2>
          <button type="button" onClick={onClose} className="text-[#6B6558] hover:text-[#1A1A1A]"><X size={20}/></button>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Name</span>
          <input value={name} onChange={(e)=>setName(e.target.value)} autoFocus data-testid="campaign-name-input"
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm"
            placeholder="e.g. New client onboarding" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Description (optional)</span>
          <textarea value={description} onChange={(e)=>setDescription(e.target.value)}
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm h-20"
            placeholder="What this campaign does…" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-[#6B6558]">Audience</span>
            <select value={audience} onChange={(e)=>setAudience(e.target.value)}
              className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm">
              <option value="mixed">Mixed</option>
              <option value="contacts">Contacts only</option>
              <option value="clients">Clients only</option>
              <option value="prospects">Prospects only</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-[#6B6558]">Daily send limit</span>
            <input type="number" min={1} max={10000} value={dailyLimit}
              onChange={(e)=>setDailyLimit(e.target.value)}
              data-testid="campaign-daily-limit-input"
              className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
          </label>
        </div>
        <div className="rounded-lg border border-[#E8DFC6] bg-white/60 px-3 py-2 space-y-2">
          <label className="flex items-center gap-2 text-sm text-[#1A1A1A]">
            <input type="checkbox" checked={weekdaysOnly}
              onChange={(e)=>setWeekdaysOnly(e.target.checked)}
              data-testid="campaign-weekdays-only-toggle" />
            Send Mon–Fri only (no weekends)
          </label>
          {weekdaysOnly && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-[#6B6558]">Timezone (IANA)</span>
              <input value={timezone} onChange={(e)=>setTimezone(e.target.value)}
                data-testid="campaign-timezone-input"
                className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-1.5 text-xs font-mono"
                placeholder="America/Chicago" />
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#6B6558]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="create-campaign-submit"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Plus size={14}/>}
            Create
          </button>
        </div>
      </form>
    </div>
  );
}


// ============================================================
// Detail drawer (steps + enrollments + stats)
// ============================================================
function CampaignDetailDrawer({ campaign, onClose, onChange }) {
  const [tab, setTab] = useState("steps");   // steps | enrollments | stats
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAddStep, setShowAddStep] = useState(false);
  const [editingStep, setEditingStep] = useState(null);   // step object being edited
  const [showEnroll, setShowEnroll] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if (tab === "enrollments") {
      api.get(`/admin/campaigns/${campaign.id}/enrollments`)
        .then(r => setEnrollments(r.data.enrollments || []))
        .catch(() => {});
    } else if (tab === "stats") {
      api.get(`/admin/campaigns/${campaign.id}/stats`)
        .then(r => setStats(r.data))
        .catch(() => {});
    }
  }, [tab, campaign.id]);

  const transition = async (action) => {
    try {
      await api.post(`/admin/campaigns/${campaign.id}/${action}`);
      toast.success(`Campaign ${action}d`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${action}`);
    }
  };

  const deleteCampaign = async () => {
    // Type-to-confirm — prevents accidental permanent deletion. Include the
    // exact campaign name so muscle-memory clicks don't wipe a real drip.
    const expected = campaign.name || "";
    const ok = window.prompt(
      `Permanently delete this campaign?\n\nType the campaign name to confirm:\n\n${expected}`,
    );
    if (ok == null) return;                       // canceled
    if ((ok || "").trim() !== expected.trim()) {
      toast.error("Name didn't match — delete canceled.");
      return;
    }
    // First attempt without force; server returns 409 if live enrollments exist,
    // which we then re-prompt to allow force-delete.
    try {
      const r = await api.delete(`/admin/campaigns/${campaign.id}`);
      toast.success(
        `Campaign deleted (${r.data.steps_deleted} step(s), ${r.data.enrollments_deleted} enrollment(s) removed)`
      );
      onClose();
      onChange();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(
          `${e.response.data.detail}\n\nDelete anyway? Any in-flight sends will be canceled.`
        )) {
          try {
            const r2 = await api.delete(`/admin/campaigns/${campaign.id}?force=true`);
            toast.success(
              `Campaign force-deleted (${r2.data.steps_deleted} step(s), ${r2.data.enrollments_deleted} enrollment(s) removed)`
            );
            onClose();
            onChange();
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Delete failed");
          }
        }
        return;
      }
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const removeStep = async (sid) => {
    if (!window.confirm("Remove this step?")) return;
    try {
      await api.delete(`/admin/campaigns/${campaign.id}/steps/${sid}`);
      toast.success("Step removed");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove step");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="campaign-detail-drawer">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#F5F1E8] h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#F5F1E8] border-b border-[#E8DFC6] px-6 py-4 flex items-start justify-between z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-serif text-xl text-[#1A1A1A] truncate">{campaign.name}</h2>
              <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[campaign.status]}`}>
                {campaign.status}
              </span>
            </div>
            {campaign.description && (
              <div className="text-xs text-[#6B6558] mt-1 truncate">{campaign.description}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSettings(true)} data-testid="edit-campaign-settings-btn"
              title="Edit campaign settings"
              className="inline-flex items-center gap-1 text-xs bg-white border border-[#D6CDB8] text-[#6B6558] hover:text-[#1A1A1A] px-3 py-1.5 rounded-full">
              <Settings size={12}/> Settings
            </button>
            {campaign.status === "draft" && (
              <button onClick={() => transition("activate")}
                className="inline-flex items-center gap-1 text-xs bg-[#245C25] text-white px-3 py-1.5 rounded-full"
                data-testid="activate-campaign-btn">
                <Play size={12}/> Activate
              </button>
            )}
            {campaign.status === "active" && (
              <button onClick={() => transition("pause")}
                className="inline-flex items-center gap-1 text-xs bg-[#7A5410] text-white px-3 py-1.5 rounded-full">
                <Pause size={12}/> Pause
              </button>
            )}
            {campaign.status === "paused" && (
              <button onClick={() => transition("activate")}
                className="inline-flex items-center gap-1 text-xs bg-[#245C25] text-white px-3 py-1.5 rounded-full">
                <Play size={12}/> Resume
              </button>
            )}
            {campaign.status !== "archived" && (
              <button onClick={() => {
                if (window.confirm("Archive this campaign? All active enrollments will be unenrolled.")) transition("archive");
              }} className="inline-flex items-center gap-1 text-xs bg-[#8A1F1A] text-white px-3 py-1.5 rounded-full"
                data-testid="archive-campaign-btn">
                <Archive size={12}/> Archive
              </button>
            )}
            <button onClick={deleteCampaign}
              title="Permanently delete this campaign"
              className="inline-flex items-center gap-1 text-xs bg-white border border-[#E38380] text-[#8A1F1A] hover:bg-[#FADCDA] px-3 py-1.5 rounded-full"
              data-testid="delete-campaign-btn">
              <Trash2 size={12}/> Delete
            </button>
            <button onClick={onClose} className="text-[#6B6558] hover:text-[#1A1A1A] p-1"><X size={20}/></button>
          </div>
        </div>

        <div className="flex gap-4 px-6 py-3 border-b border-[#E8DFC6] bg-[#F0EBDC]">
          {["steps","enrollments","stats"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`text-sm capitalize pb-1 transition ${tab===t?"text-[#1A1A1A] border-b-2 border-[#8B4513]":"text-[#6B6558] hover:text-[#1A1A1A]"}`}
              data-testid={`tab-${t}`}>
              {t}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            {tab === "steps" && (
              <button onClick={() => setShowAddStep(true)} data-testid="add-step-btn"
                className="text-xs inline-flex items-center gap-1 text-[#8B4513] hover:underline">
                <Plus size={12}/> Add step
              </button>
            )}
            {tab === "enrollments" && (
              <button onClick={() => setShowEnroll(true)} data-testid="enroll-btn"
                className="text-xs inline-flex items-center gap-1 text-[#8B4513] hover:underline">
                <Users size={12}/> Enroll people
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          {tab === "steps" && (
            campaign.steps.length === 0 ? (
              <div className="text-sm text-[#6B6558]">No steps yet. Add the first email step to get started.</div>
            ) : (
              <div className="space-y-3">
                {campaign.steps.map((s, i) => (
                  <div key={s.id} className="bg-white rounded-xl border border-[#E8DFC6] p-4"
                    data-testid={`step-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs bg-[#F2ECE0] text-[#6B6558] px-2 py-0.5 rounded-full">Step {i+1}</span>
                          {s.send_at ? (
                            <span className="text-xs text-[#6B6558] inline-flex items-center gap-1">
                              <Calendar size={11}/> {new Date(s.send_at).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-xs text-[#6B6558] inline-flex items-center gap-1">
                              <Clock size={11}/> {s.delay_days === 0 ? "Immediately on enroll" : `${s.delay_days} day${s.delay_days===1?"":"s"} after enroll`}
                            </span>
                          )}
                          {!s.active && <span className="text-[10px] text-[#8A1F1A] uppercase">Inactive</span>}
                        </div>
                        <button
                          onClick={() => setEditingStep(s)}
                          className="text-sm font-medium text-[#1A1A1A] text-left hover:underline"
                          data-testid={`step-open-${s.id}`}
                          title="Click to view or edit this email"
                        >
                          {s.subject}
                        </button>
                        <div className="text-xs text-[#6B6558] mt-1 whitespace-pre-wrap line-clamp-3">{s.body_text}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditingStep(s)}
                          title="View & edit email"
                          className="text-[#6B6558] hover:text-[#1A1A1A] p-1"
                          data-testid={`edit-step-${s.id}`}>
                          <Edit3 size={14}/>
                        </button>
                        <button onClick={() => removeStep(s.id)} className="text-[#8A1F1A] hover:text-[#5c1512] p-1"
                          data-testid={`remove-step-${s.id}`}>
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "enrollments" && (
            enrollments.length === 0 ? (
              <div className="text-sm text-[#6B6558]">No one enrolled yet. Click "Enroll people" to add contacts, clients, or prospects.</div>
            ) : (
              <div className="space-y-2">
                {enrollments.map(e => (
                  <div key={e.id} className="bg-white rounded-lg border border-[#E8DFC6] p-3 flex items-center justify-between"
                    data-testid={`enrollment-${e.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#1A1A1A] truncate">{e.subject_email}</div>
                      <div className="text-xs text-[#6B6558]">
                        {e.subject_type} • step {(e.current_step_idx ?? 0) + 1} • {e.status}
                        {e.next_send_at && e.status === "active" && ` • next: ${new Date(e.next_send_at).toLocaleString()}`}
                      </div>
                    </div>
                    <EnrollmentActions e={e} onChange={onChange} />
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "stats" && (
            !stats ? (
              <div className="text-sm text-[#6B6558]">Loading…</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Enrolled total" value={stats.enrollments_total}/>
                <StatCard label="Sends today" value={stats.sent_today}/>
                <StatCard label="Sends OK" value={stats.sends_ok}/>
                <StatCard label="Sends failed" value={stats.sends_failed}/>
                <div className="col-span-2 bg-white rounded-lg border border-[#E8DFC6] p-4">
                  <div className="text-xs uppercase text-[#6B6558] mb-2">Enrollments by status</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {Object.entries(stats.enrollments_by_status || {}).map(([k,v]) => (
                      <div key={k} className="flex justify-between border-b border-[#F2ECE0] py-1">
                        <span className="text-[#6B6558]">{k}</span>
                        <span className="text-[#1A1A1A] font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {showAddStep && (
        <AddStepDialog campaignId={campaign.id}
          nextOrder={campaign.steps.length}
          onClose={() => setShowAddStep(false)}
          onAdded={() => { setShowAddStep(false); onChange(); }} />
      )}
      {editingStep && (
        <StepEditDialog campaignId={campaign.id} step={editingStep}
          stepIndex={campaign.steps.findIndex((x) => x.id === editingStep.id)}
          onClose={() => setEditingStep(null)}
          onSaved={() => { setEditingStep(null); onChange(); }} />
      )}
      {showEnroll && (
        <EnrollDialog campaignId={campaign.id}
          onClose={() => setShowEnroll(false)}
          onEnrolled={() => { setShowEnroll(false); onChange(); }} />
      )}
      {showSettings && (
        <SettingsDialog campaign={campaign}
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); onChange(); }} />
      )}
    </div>
  );
}


// ============================================================
// Settings dialog — edit daily_send_limit, weekday-only window,
// timezone, sender fields on an existing campaign. Preserves
// status/steps/enrollments (server-side guarantee).
// ============================================================
function SettingsDialog({ campaign, onClose, onSaved }) {
  const [name, setName] = useState(campaign.name || "");
  const [description, setDescription] = useState(campaign.description || "");
  const [fromEmail, setFromEmail] = useState(campaign.from_email || "");
  const [fromName, setFromName] = useState(campaign.from_name || "");
  const [replyTo, setReplyTo] = useState(campaign.reply_to || "");
  const [dailyLimit, setDailyLimit] = useState(campaign.daily_send_limit ?? 500);
  const [weekdaysOnly, setWeekdaysOnly] = useState(!!campaign.send_weekdays_only);
  const [timezone, setTimezone] = useState(campaign.send_timezone || "America/Chicago");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        description: description,
        from_email: fromEmail.trim() || null,
        from_name: fromName.trim() || null,
        reply_to: replyTo.trim() || null,
        daily_send_limit: Number(dailyLimit),
        send_weekdays_only: weekdaysOnly,
        send_timezone: weekdaysOnly ? (timezone.trim() || null) : null,
      };
      await api.patch(`/admin/campaigns/${campaign.id}`, payload);
      toast.success("Settings saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save settings");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      data-testid="campaign-settings-dialog">
      <form onSubmit={submit} className="bg-[#F5F1E8] rounded-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-serif text-[#1A1A1A]">Campaign settings</h2>
          <button type="button" onClick={onClose} className="text-[#6B6558] hover:text-[#1A1A1A]"><X size={20}/></button>
        </div>
        <div className="text-[11px] text-[#6B6558] font-mono truncate">id: {campaign.id}</div>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Name</span>
          <input value={name} onChange={(e)=>setName(e.target.value)} data-testid="settings-name-input"
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Description</span>
          <textarea value={description} onChange={(e)=>setDescription(e.target.value)}
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm h-16" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-[#6B6558]">From name</span>
            <input value={fromName} onChange={(e)=>setFromName(e.target.value)}
              className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Wayne Byrd" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-[#6B6558]">From email</span>
            <input value={fromEmail} onChange={(e)=>setFromEmail(e.target.value)}
              className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm"
              placeholder="wayne@byrd-co.com" />
          </label>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Reply-to</span>
          <input value={replyTo} onChange={(e)=>setReplyTo(e.target.value)}
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Daily send limit</span>
          <input type="number" min={1} max={10000} value={dailyLimit}
            onChange={(e)=>setDailyLimit(e.target.value)}
            data-testid="settings-daily-limit-input"
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
          <span className="text-[10px] text-[#8A8471]">Max emails sent from this campaign per day (1–10,000).</span>
        </label>
        <div className="rounded-lg border border-[#E8DFC6] bg-white/60 px-3 py-2 space-y-2">
          <label className="flex items-center gap-2 text-sm text-[#1A1A1A]">
            <input type="checkbox" checked={weekdaysOnly}
              onChange={(e)=>setWeekdaysOnly(e.target.checked)}
              data-testid="settings-weekdays-only-toggle" />
            Send Mon–Fri only (no weekends)
          </label>
          {weekdaysOnly && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wide text-[#6B6558]">Timezone (IANA)</span>
              <input value={timezone} onChange={(e)=>setTimezone(e.target.value)}
                data-testid="settings-timezone-input"
                className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-1.5 text-xs font-mono"
                placeholder="America/Chicago" />
              <span className="text-[10px] text-[#8A8471]">Weekend-due sends will shift to Monday at the same local time.</span>
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#6B6558]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="settings-save-btn"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}


function EnrollmentActions({ e, onChange }) {
  const act = async (path) => {
    try {
      await api.post(`/admin/enrollments/${e.id}/${path}`);
      toast.success("Updated");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      {e.status === "active" && (
        <button onClick={() => act("pause")} className="text-xs px-2 py-1 rounded hover:bg-[#F0EBDC] text-[#7A5410]"
          title="Pause"><Pause size={12}/></button>
      )}
      {e.status === "paused" && (
        <button onClick={() => act("resume")} className="text-xs px-2 py-1 rounded hover:bg-[#F0EBDC] text-[#245C25]"
          title="Resume"><Play size={12}/></button>
      )}
      {(e.status === "active" || e.status === "paused") && (
        <button onClick={() => act("unenroll")} className="text-xs px-2 py-1 rounded hover:bg-[#F0EBDC] text-[#8A1F1A]"
          title="Unenroll"><X size={12}/></button>
      )}
    </div>
  );
}


function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-lg border border-[#E8DFC6] p-4">
      <div className="text-xs uppercase text-[#6B6558]">{label}</div>
      <div className="text-2xl font-serif text-[#1A1A1A] mt-1">{value}</div>
    </div>
  );
}


function AddStepDialog({ campaignId, nextOrder, onClose, onAdded }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [mode, setMode] = useState("delay");   // delay | absolute
  const [delay, setDelay] = useState(nextOrder === 0 ? 0 : 3);
  const [sendAt, setSendAt] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body are required");
    setBusy(true);
    try {
      const payload = { subject: subject.trim(), body_text: body, active: true };
      if (mode === "delay") payload.delay_days = parseInt(delay || 0, 10);
      else payload.send_at = new Date(sendAt).toISOString();
      await api.post(`/admin/campaigns/${campaignId}/steps`, payload);
      toast.success("Step added");
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add step");
    } finally { setBusy(false); }
  };

  const subjectRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const [lastFocus, setLastFocus] = useState("body");
  const insertToken = (tok) => {
    const target = lastFocus === "subject" ? subjectRef.current : bodyRef.current;
    if (!target) return;
    const val = target === subjectRef.current ? subject : body;
    const setter = target === subjectRef.current ? setSubject : setBody;
    const start = target.selectionStart ?? val.length;
    const end = target.selectionEnd ?? val.length;
    const insertion = `{{${tok}}}`;
    const next = val.substring(0, start) + insertion + val.substring(end);
    setter(next);
    // Restore focus + place cursor after the inserted token on next tick
    setTimeout(() => {
      target.focus();
      const pos = start + insertion.length;
      target.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="add-step-dialog">
      <form onSubmit={submit} className="bg-[#F5F1E8] rounded-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg">Add step #{nextOrder + 1}</h3>
          <button type="button" onClick={onClose}><X size={20}/></button>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Subject</span>
          <input ref={subjectRef} value={subject} onChange={(e)=>setSubject(e.target.value)}
            onFocus={() => setLastFocus("subject")} data-testid="step-subject-input"
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm"
            placeholder="Hey {{first_name}}, quick note" />
        </label>
        <TokenBar onInsert={insertToken} />
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Body</span>
          <textarea ref={bodyRef} value={body} onChange={(e)=>setBody(e.target.value)}
            onFocus={() => setLastFocus("body")} data-testid="step-body-input"
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm h-40 font-mono"
            placeholder={"Hey {{first_name}},\n\nThanks for the intro at {{company_name}}…"} />
          <div className="text-[11px] text-[#6B6558] mt-1">
            Click a token chip above to insert into whichever field was last focused. Unsubscribe footer auto-appended.
          </div>
        </label>
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-[#6B6558]">Schedule</div>
          <div className="flex gap-4">
            <label className="text-sm inline-flex items-center gap-2">
              <input type="radio" checked={mode==="delay"} onChange={()=>setMode("delay")} />
              Days after enrollment
            </label>
            <label className="text-sm inline-flex items-center gap-2">
              <input type="radio" checked={mode==="absolute"} onChange={()=>setMode("absolute")} />
              Specific date/time
            </label>
          </div>
          {mode === "delay" ? (
            <input type="number" min="0" max="730" value={delay} onChange={(e)=>setDelay(e.target.value)}
              data-testid="step-delay-input"
              className="w-32 bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm"
              placeholder="0" />
          ) : (
            <input type="datetime-local" value={sendAt} onChange={(e)=>setSendAt(e.target.value)}
              data-testid="step-sendat-input"
              className="w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#6B6558]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="step-submit"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Plus size={14}/>}
            Add step
          </button>
        </div>
      </form>
    </div>
  );
}


function EnrollDialog({ campaignId, onClose, onEnrolled }) {
  const [mode, setMode] = useState("pick");   // "pick" | "csv"
  const [audience, setAudience] = useState("contact");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  // CSV mode state
  const [csvFile, setCsvFile] = useState(null);
  const [csvInspect, setCsvInspect] = useState(null);       // headers + auto-detected + preview
  const [csvMapping, setCsvMapping] = useState({});          // { canonical_field: header_name }
  const [csvResult, setCsvResult] = useState(null);

  useEffect(() => {
    if (mode !== "pick") return;
    const endpoint = audience === "client" ? "/admin/clients"
                   : audience === "prospect" ? "/admin/marketplace/prospects"
                   : "/admin/contacts";
    api.get(endpoint).then(r => {
      let list = Array.isArray(r.data) ? r.data : (r.data.prospects || r.data.contacts || []);
      // Normalise fields
      list = list.map(x => ({
        id: x.id,
        name: x.name || x.contact_name || x.institution || x.email,
        email: x.email || x.contact_email,
      })).filter(x => x.email);
      setRows(list);
      setSelected(new Set());
    }).catch(() => setRows([]));
  }, [audience, mode]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const lc = q.toLowerCase();
    return rows.filter(r => (r.name||"").toLowerCase().includes(lc) || (r.email||"").toLowerCase().includes(lc));
  }, [rows, q]);

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const submit = async () => {
    if (selected.size === 0) return toast.error("Pick at least one recipient");
    setBusy(true);
    try {
      const subjects = Array.from(selected).map(id => ({ subject_type: audience, subject_id: id }));
      const r = await api.post(`/admin/campaigns/${campaignId}/enroll`, { subjects });
      toast.success(`${r.data.enrolled} enrolled${r.data.skipped ? ` • ${r.data.skipped} skipped` : ""}`);
      onEnrolled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enroll failed");
    } finally { setBusy(false); }
  };

  const uploadCsv = async () => {
    if (!csvFile) return toast.error("Pick a CSV file first");
    if (csvFile.size > 5 * 1024 * 1024) return toast.error("CSV too large (5 MB max)");
    setBusy(true);
    setCsvResult(null);
    try {
      const form = new FormData();
      form.append("file", csvFile, csvFile.name);
      // Attach the mapping so the server honors any manual overrides the user
      // made in the mapper UI. Falls back to auto-detect if omitted.
      Object.entries(csvMapping).forEach(([k, v]) => {
        if (v) form.append(`map_${k}`, v);
      });
      const r = await api.post(`/admin/campaigns/${campaignId}/enroll-csv`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(r.data);
      const parts = [`${r.data.enrolled} enrolled`];
      if (r.data.contacts_created) parts.push(`${r.data.contacts_created} new contacts`);
      if (r.data.skipped_dupes) parts.push(`${r.data.skipped_dupes} duplicates`);
      if (r.data.invalid) parts.push(`${r.data.invalid} invalid`);
      toast.success(parts.join(" · "));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV upload failed");
    } finally { setBusy(false); }
  };

  // Peek at the CSV first — inspect headers + preview + auto-detect mapping.
  // If we can already resolve `email`, this is invisible to the user; otherwise
  // the mapper UI surfaces so they can pick which column is which.
  const inspectCsv = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("CSV too large (5 MB max)"); return; }
    setBusy(true);
    setCsvInspect(null);
    setCsvResult(null);
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const r = await api.post("/admin/campaigns/csv-inspect", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvInspect(r.data);
      setCsvMapping(r.data.detected || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read that CSV");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    // Small helper so users don't guess the column names
    const csv = "first_name,last_name,email\nJane,Doe,jane@example.com\nJohn,Smith,john@example.com\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "campaign-enrollment-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="enroll-dialog">
      <div className="bg-[#F5F1E8] rounded-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg">Enroll into campaign</h3>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        {/* Mode toggle: pick from list vs upload CSV */}
        <div className="inline-flex rounded-full bg-white border border-[#D6CDB8] p-1 self-start">
          <button onClick={() => { setMode("pick"); setCsvResult(null); }}
            className={`text-xs px-4 py-1.5 rounded-full transition inline-flex items-center gap-1.5 ${mode==="pick"?"bg-[#1A1A1A] text-[#F5F1E8]":"text-[#6B6558]"}`}
            data-testid="enroll-mode-pick">
            <Users size={12}/> Pick from list
          </button>
          <button onClick={() => setMode("csv")}
            className={`text-xs px-4 py-1.5 rounded-full transition inline-flex items-center gap-1.5 ${mode==="csv"?"bg-[#1A1A1A] text-[#F5F1E8]":"text-[#6B6558]"}`}
            data-testid="enroll-mode-csv">
            <Upload size={12}/> Upload CSV
          </button>
        </div>

        {mode === "pick" && (
          <>
            <div className="flex gap-2">
              {["contact","client","prospect"].map(t => (
                <button key={t} onClick={()=>setAudience(t)}
                  className={`text-xs px-3 py-1.5 rounded-full transition ${audience===t?"bg-[#1A1A1A] text-[#F5F1E8]":"bg-white border border-[#D6CDB8] text-[#6B6558]"}`}
                  data-testid={`enroll-audience-${t}`}>
                  {t}s
                </button>
              ))}
            </div>
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search…"
              className="bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
            <div className="flex-1 overflow-y-auto border border-[#E8DFC6] rounded-lg bg-white min-h-[240px]">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-[#6B6558]">No matches.</div>
              ) : filtered.map(r => (
                <label key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#FBF7EE] cursor-pointer border-b border-[#F2ECE0]"
                  data-testid={`enroll-row-${r.id}`}>
                  <input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggle(r.id)} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#1A1A1A] truncate">{r.name}</div>
                    <div className="text-xs text-[#6B6558] truncate">{r.email}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-between items-center">
              <div className="text-xs text-[#6B6558]">{selected.size} selected</div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-[#6B6558]">Cancel</button>
                <button onClick={submit} disabled={busy || selected.size===0} data-testid="enroll-submit"
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>}
                  Enroll
                </button>
              </div>
            </div>
          </>
        )}

        {mode === "csv" && (
          <div className="space-y-4">
            <div className="rounded-lg border border-[#E8DFC6] bg-white p-4 space-y-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">// CSV columns</div>
                <div className="text-sm text-[#1A1A1A] mt-1">
                  Needs an <b>email</b> column at minimum. <b>first_name</b> and <b>last_name</b> are optional
                  but recommended so tokens like <code>{"{{first_name}}"}</code> render for everyone.
                </div>
              </div>
              <button onClick={downloadTemplate} data-testid="enroll-csv-template"
                className="text-xs text-[#C89434] hover:underline inline-flex items-center gap-1">
                <FileText size={12}/> Download template CSV
              </button>
            </div>
            <label className="block cursor-pointer rounded-lg border-2 border-dashed border-[#D6CDB8] p-6 text-center bg-white hover:bg-[#FBF7EE] transition"
              data-testid="enroll-csv-drop">
              <input type="file" accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setCsvFile(f);
                  setCsvResult(null);
                  if (f) inspectCsv(f);
                }}
                className="hidden" data-testid="enroll-csv-file" />
              <Upload size={28} className="mx-auto text-[#6B6558]" />
              <div className="mt-2 text-sm text-[#1A1A1A]">
                {csvFile ? csvFile.name : "Click to choose a CSV file"}
              </div>
              {csvFile && (
                <div className="text-[11px] text-[#6B6558]">{(csvFile.size/1024).toFixed(1)} KB</div>
              )}
            </label>

            {csvInspect && !csvResult && (
              <div className="rounded-lg border border-[#E8DFC6] bg-white p-4 space-y-3" data-testid="csv-mapper">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">// Column mapping</div>
                  <div className="text-sm text-[#1A1A1A] mt-1">
                    Match each field below to the column in your CSV. Auto-detected picks
                    are pre-filled — adjust only if they're wrong.
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { k: "email", label: "Email", required: true },
                    { k: "first_name", label: "First name" },
                    { k: "last_name", label: "Last name" },
                    { k: "company", label: "Company name" },
                    { k: "property_address", label: "Property address" },
                    { k: "loan_maturity_date", label: "Loan maturity date" },
                  ].map((row) => (
                    <label key={row.k} className="block">
                      <span className="text-[10px] uppercase tracking-widest text-[#6B6558]">
                        {row.label}{row.required && <span className="text-[#C89434]"> *</span>}
                      </span>
                      <select
                        value={csvMapping[row.k] || ""}
                        onChange={(e) => setCsvMapping({ ...csvMapping, [row.k]: e.target.value })}
                        data-testid={`csv-map-${row.k}`}
                        className="mt-1 w-full h-9 px-2 rounded border border-[#D6CDB8] bg-white text-sm"
                      >
                        <option value="">— none —</option>
                        {csvInspect.headers.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
                {csvInspect.preview.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-[#6B6558]">Preview first {csvInspect.preview.length} rows</summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="text-left border-b border-[#E8DFC6]">
                            {csvInspect.headers.map((h) => (
                              <th key={h} className="p-1.5 font-mono text-[#6B6558]">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvInspect.preview.map((row, i) => (
                            <tr key={i} className="border-b border-[#F3EEE0]">
                              {csvInspect.headers.map((h) => (
                                <td key={h} className="p-1.5 truncate max-w-[180px]">{row[h] || "—"}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
                {!csvMapping.email && (
                  <div className="text-xs text-[#8A1F1A] bg-[#FBECEB] border border-[#8A1F1A]/30 rounded p-2">
                    Pick which column holds the recipient's email address before importing.
                  </div>
                )}
              </div>
            )}

            {csvResult && (
              <div className="rounded-lg border border-[#E8DFC6] bg-white p-4" data-testid="enroll-csv-result">
                <div className="text-sm font-medium text-[#1A1A1A]">Import summary</div>
                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                  <SummaryStat label="Enrolled" value={csvResult.enrolled} tone="ok" />
                  <SummaryStat label="New contacts" value={csvResult.contacts_created} />
                  <SummaryStat label="Duplicates skipped" value={csvResult.skipped_dupes} tone={csvResult.skipped_dupes ? "warn" : "muted"} />
                  <SummaryStat label="Invalid rows" value={csvResult.invalid} tone={csvResult.invalid ? "bad" : "muted"} />
                </div>
                {csvResult.invalid > 0 && (
                  <details className="mt-3 text-xs text-[#6B6558]">
                    <summary className="cursor-pointer">Show invalid rows</summary>
                    <ul className="mt-1 space-y-0.5">
                      {csvResult.results.filter(r => !r.ok && r.reason !== "already_enrolled" && r.reason !== "duplicate_in_file").slice(0, 20).map((r, i) => (
                        <li key={i}>Row {r.row}: {r.email || "(no email)"} — {r.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-[#E8DFC6]">
              <button onClick={onClose} className="px-4 py-2 text-sm text-[#6B6558]">
                {csvResult ? "Done" : "Cancel"}
              </button>
              {!csvResult && (
                <button onClick={uploadCsv} disabled={busy || !csvFile || !csvMapping.email} data-testid="enroll-csv-upload"
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>}
                  Import &amp; enroll
                </button>
              )}
              {csvResult && (
                <button onClick={() => { setCsvFile(null); setCsvResult(null); onEnrolled(); }}
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm">
                  <Check size={14}/> Close
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================
// STEP EDIT DIALOG — read + edit an existing step's email
// ============================================================
function StepEditDialog({ campaignId, step, stepIndex, onClose, onSaved }) {
  const [subject, setSubject] = useState(step.subject || "");
  const [body, setBody] = useState(step.body_text || "");
  const [mode, setMode] = useState(step.send_at ? "absolute" : "delay");
  const [delay, setDelay] = useState(step.delay_days ?? 0);
  const [sendAt, setSendAt] = useState(
    step.send_at ? new Date(step.send_at).toISOString().slice(0, 16) : ""
  );
  const [active, setActive] = useState(step.active !== false);
  const [busy, setBusy] = useState(false);

  const subjectRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const [lastFocus, setLastFocus] = useState("body");
  const insertToken = (tok) => {
    const target = lastFocus === "subject" ? subjectRef.current : bodyRef.current;
    if (!target) return;
    const val = target === subjectRef.current ? subject : body;
    const setter = target === subjectRef.current ? setSubject : setBody;
    const start = target.selectionStart ?? val.length;
    const end = target.selectionEnd ?? val.length;
    const insertion = `{{${tok}}}`;
    const next = val.substring(0, start) + insertion + val.substring(end);
    setter(next);
    setTimeout(() => {
      target.focus();
      const pos = start + insertion.length;
      target.setSelectionRange(pos, pos);
    }, 0);
  };

  const save = async (e) => {
    e && e.preventDefault();
    if (!subject.trim() || !body.trim()) return toast.error("Subject and body are required");
    setBusy(true);
    try {
      const payload = {
        subject: subject.trim(),
        body_text: body,
        // Clear body_html so the server regenerates from body_text — otherwise
        // our text edits and the cached HTML would drift out of sync.
        body_html: null,
        active,
      };
      if (mode === "delay") {
        payload.delay_days = parseInt(delay || 0, 10);
        payload.send_at = null;
      } else {
        payload.send_at = new Date(sendAt).toISOString();
      }
      await api.patch(`/admin/campaigns/${campaignId}/steps/${step.id}`, payload);
      toast.success("Step saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="step-edit-dialog">
      <form onSubmit={save} className="bg-[#F5F1E8] rounded-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">// Step {stepIndex + 1}</div>
            <h3 className="font-serif text-lg">Edit email</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="step-edit-close"><X size={20}/></button>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Subject</span>
          <input ref={subjectRef} value={subject} onChange={(e) => setSubject(e.target.value)}
            onFocus={() => setLastFocus("subject")}
            data-testid="step-edit-subject"
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
        </label>
        <TokenBar onInsert={insertToken} />
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-[#6B6558]">Body</span>
          <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)}
            onFocus={() => setLastFocus("body")}
            data-testid="step-edit-body"
            className="mt-1 w-full bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm h-60 font-mono" />
          <div className="text-[11px] text-[#6B6558] mt-1">
            Click a token chip above to insert into whichever field was last focused.
            Signature &amp; unsubscribe footer auto-appended at send time.
          </div>
        </label>
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-[#6B6558]">Schedule</div>
          <div className="flex gap-4">
            <label className="text-sm inline-flex items-center gap-2">
              <input type="radio" checked={mode==="delay"} onChange={() => setMode("delay")} />
              Days after enrollment
            </label>
            <label className="text-sm inline-flex items-center gap-2">
              <input type="radio" checked={mode==="absolute"} onChange={() => setMode("absolute")} />
              Specific date/time
            </label>
          </div>
          {mode === "delay" ? (
            <input type="number" min="0" max="730" value={delay} onChange={(e) => setDelay(e.target.value)}
              data-testid="step-edit-delay"
              className="w-32 bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
          ) : (
            <input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
              data-testid="step-edit-sendat"
              className="bg-white border border-[#D6CDB8] rounded-lg px-3 py-2 text-sm" />
          )}
        </div>
        <label className="text-sm inline-flex items-center gap-2">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
            data-testid="step-edit-active" />
          Active
        </label>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#E8DFC6]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#6B6558]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="step-edit-save"
            className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F5F1E8] px-4 py-2 rounded-full text-sm disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}



function SummaryStat({ label, value, tone = "muted" }) {
  const toneClass = tone === "ok" ? "text-[#2C7A2B] border-[#2C7A2B]/30 bg-[#EDF6EC]"
                  : tone === "bad" ? "text-[#8A1F1A] border-[#8A1F1A]/30 bg-[#FBECEB]"
                  : tone === "warn" ? "text-[#8A5A1F] border-[#8A5A1F]/30 bg-[#FBF3E5]"
                  : "text-[#6B6558] border-[#E4DFD1] bg-[#F9F6EE]";
  return (
    <div className={`rounded-md border p-2 text-center ${toneClass}`}>
      <div className="text-lg font-bold leading-none">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest mt-0.5">{label}</div>
    </div>
  );
}



// Insertable merge-token chips shown above the body field in both AddStep and
// StepEdit dialogs. Clicking a chip drops `{{token}}` at the caret of whichever
// field (subject or body) was last focused. This is the discoverable path — the
// help text below the body just describes tokens; this actually inserts them.
const TOKEN_CHIPS = [
  { tok: "first_name", label: "First name" },
  { tok: "last_name", label: "Last name" },
  { tok: "full_name", label: "Full name" },
  { tok: "company_name", label: "Company" },
  { tok: "property_address", label: "Property address" },
  { tok: "loan_maturity_date", label: "Loan maturity date" },
  { tok: "sender_name", label: "Sender name" },
];

function TokenBar({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="token-bar">
      <span className="text-[10px] uppercase tracking-widest text-[#6B6558] self-center pr-1">
        Insert:
      </span>
      {TOKEN_CHIPS.map((t) => (
        <button
          key={t.tok}
          type="button"
          onClick={() => onInsert(t.tok)}
          data-testid={`token-chip-${t.tok}`}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-white border border-[#D6CDB8] text-[#6B6558] hover:bg-[#F2ECE0] hover:text-[#1A1A1A] transition"
          title={`Insert {{${t.tok}}}`}
        >
          <span className="font-mono text-[#C89434]">&#123;&#123;</span>
          {t.label}
          <span className="font-mono text-[#C89434]">&#125;&#125;</span>
        </button>
      ))}
    </div>
  );
}

